import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, ConfirmModal, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { courseTabHref } from '@/lib/course-admin-navigation';
import { roleActionErrorToast } from '@/lib/user-feedback';

export interface CourseCatalogRowProps {
  id: string;
  name: string;
  code?: string;
  shortDescription?: string;
  status?: string;
  statusLabel: string;
  categoryName?: string;
  instructorName?: string;
  trashed?: boolean;
}

function statusVariant(status: string): 'success' | 'warning' | 'default' {
  if (status === 'published') return 'success';
  if (status === 'archived') return 'default';
  return 'warning';
}

export function CourseCatalogRow({
  id,
  name,
  code,
  shortDescription,
  status = 'draft',
  statusLabel,
  categoryName,
  instructorName,
  trashed,
}: CourseCatalogRowProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const canEditCourse = hasPermission(PERMISSIONS.CURSOS_EDITAR);
  const canCreateCourse = hasPermission(PERMISSIONS.CURSOS_CREAR);
  const canDeleteCourse = hasPermission(PERMISSIONS.CURSOS_ELIMINAR);
  const showUrl = courseTabHref(id, 'detalle', searchParams);
  const editUrl = courseTabHref(id, 'configuracion', searchParams);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['courses'] });
  };

  const duplicateCourse = useMutation({
    mutationFn: () => api<{ id: string }>(`/courses/${id}/duplicate`, { method: 'POST' }),
    onSuccess: (duplicate) => {
      invalidate();
      navigate(courseTabHref(duplicate.id, 'configuracion', searchParams));
    },
  });

  const removeCourse = useMutation({
    mutationFn: () => api(`/courses/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      invalidate();
    },
    onError: (error) => {
      setDeleteOpen(false);
      toast(roleActionErrorToast(error, 'No se pudo eliminar el curso'));
    },
  });

  const pending = duplicateCourse.isPending || removeCourse.isPending;
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const codeLabel = code?.trim() ? code : null;
  const description = shortDescription?.trim() ? shortDescription.trim() : null;

  return (
    <>
      <tr>
        <th className="lms-gradebook__td" scope="row">
          <div className="lms-gradebook__student">
            <div className="lms-gradebook__avatar" aria-hidden="true">
              {initial}
            </div>
            <div className="min-w-0">
              <Link to={showUrl} className="lms-gradebook__name">
                {name}
              </Link>
              {codeLabel ? <div className="lms-gradebook__muted">{codeLabel}</div> : null}
              {description ? <div className="lms-gradebook__muted">{description}</div> : null}
            </div>
          </div>
        </th>
        <td className="lms-gradebook__td">
          {categoryName ?? <span className="lms-gradebook__muted">—</span>}
        </td>
        <td className="lms-gradebook__td">
          <Badge variant={statusVariant(status)}>{statusLabel}</Badge>
        </td>
        <td className="lms-gradebook__td">
          {instructorName ?? <span className="lms-gradebook__muted">Sin instructor</span>}
        </td>
        <td className="lms-gradebook__td lms-gradebook__td--actions">
          <div className="flex items-center justify-end gap-1">
            {!trashed ? (
              <>
                <Link to={showUrl} className="lms-gradebook__action" title="Ver" aria-label={`Ver ${name}`}>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    visibility
                  </span>
                </Link>
                {canEditCourse && (
                  <Link to={editUrl} className="lms-gradebook__action" title="Editar" aria-label={`Editar ${name}`}>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      edit
                    </span>
                  </Link>
                )}
                {canCreateCourse && (
                  <button
                    type="button"
                    className="lms-gradebook__action"
                    title="Duplicar"
                    aria-label={`Duplicar ${name}`}
                    disabled={pending}
                    onClick={() => duplicateCourse.mutate()}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      content_copy
                    </span>
                  </button>
                )}
                {canDeleteCourse && (
                  <button
                    type="button"
                    className="lms-gradebook__action"
                    title="Eliminar"
                    aria-label={`Eliminar ${name}`}
                    disabled={pending}
                    onClick={() => setDeleteOpen(true)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      delete
                    </span>
                  </button>
                )}
              </>
            ) : canDeleteCourse ? (
              <button
                type="button"
                className="lms-gradebook__action"
                title="Eliminar permanentemente"
                aria-label={`Eliminar permanentemente ${name}`}
                disabled={pending}
                onClick={() => setDeleteOpen(true)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  delete
                </span>
              </button>
            ) : null}
          </div>
        </td>
      </tr>

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar curso"
        description={`¿Eliminar el curso «${name}»? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        loading={removeCourse.isPending}
        onConfirm={() => removeCourse.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  );
}
